import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Container,
  Divider,
  Fade,
  Zoom,
  MenuItem,
  Chip,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Alert,
} from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import Logo from "../components/Logo";
import "../styles/global.css";

// ===== ANIMATIONS =====
const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-8px); }
  100% { transform: translateY(0px); }
`;

const pulseGlow = keyframes`
  0% { box-shadow: 0 0 20px rgba(255,215,0,0.1); }
  50% { box-shadow: 0 0 40px rgba(255,215,0,0.2); }
  100% { box-shadow: 0 0 20px rgba(255,215,0,0.1); }
`;

// ===== STYLED COMPONENTS =====
const GlassTextField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    color: "#ffffff",
    borderRadius: 2,
    background: "rgba(255,255,255,0.03)",
    transition: "all 0.3s ease",
    "& fieldset": {
      borderColor: "rgba(255,255,255,0.08)",
      borderWidth: 1.5,
    },
    "&:hover fieldset": {
      borderColor: "rgba(255,215,0,0.3)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#ffd700",
      borderWidth: 2,
      boxShadow: "0 0 0 4px rgba(255,215,0,0.05)",
    },
  },
  "& .MuiInputLabel-root": {
    color: "rgba(255,255,255,0.4)",
    "&.Mui-focused": {
      color: "#ffd700",
    },
  },
  "& .MuiInputBase-inputMultiline": {
    color: "#ffffff",
  },
  "& .MuiSelect-select": {
    color: "#ffffff",
  },
  "& .MuiMenuItem-root": {
    color: "#ffffff",
  },
});

const LeftPanel = styled(Box)({
  position: "relative",
  height: "100%",
  minHeight: "600px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  overflow: "hidden",
  background: "#0a0e1a",
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    backgroundImage: `url(/mightymenofdavid.png)`,
    backgroundSize: "contain",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    zIndex: 0,
  },
  "&::after": {
    content: '""',
    position: "absolute",
    inset: 0,
    background: "linear-gradient(160deg, rgba(10, 14, 26, 0.3) 0%, rgba(20, 25, 50, 0.2) 50%, rgba(36, 51, 138, 0.15) 100%)",
    zIndex: 1,
  },
});

const LogoContainer = styled(Box)({
  position: "absolute",
  top: 30,
  left: 30,
  zIndex: 2,
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
});

const Application = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [cvFile, setCvFile] = useState(null);
  const [fileName, setFileName] = useState("");
  
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    age: "",
    dateOfBirth: "",
    nationality: "",
    location: "",
    gender: "",
    currentPosition: "",
    company: "",
    experience: "",
    education: "",
    skills: "",
    languages: "",
    whyJoin: "",
    contribution: "",
    leadershipExperience: "",
    termsAccepted: false,
    references: "",
  });

  const steps = [
    {
      label: "Personal Information",
      description: "Tell us about yourself",
    },
    {
      label: "Professional Background",
      description: "Your experience and skills",
    },
    {
      label: "Motivation & Commitment",
      description: "Why you want to join",
    },
    {
      label: "Review & Submit",
      description: "Finalize your application",
    },
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setCvFile(file);
      setFileName(file.name);
    }
  };

  const handleNext = () => {
    setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  // Function to send email using mailto
  const sendEmail = () => {
    const subject = encodeURIComponent("300 Mighty Men Application - " + formData.fullName);
    
    // Format the email body with all form data
    const body = encodeURIComponent(
      `Application for the 300 Mighty Men Program\n\n` +
      `===========================================\n\n` +
      `PERSONAL INFORMATION\n` +
      `--------------------\n` +
      `Full Name: ${formData.fullName}\n` +
      `Email: ${formData.email}\n` +
      `Phone: ${formData.phone}\n` +
      `Date of Birth: ${formData.dateOfBirth || "Not provided"}\n` +
      `Age: ${formData.age || "Not provided"}\n` +
      `Nationality: ${formData.nationality || "Not provided"}\n` +
      `Location: ${formData.location}\n` +
      `Gender: ${formData.gender || "Not provided"}\n\n` +
      
      `PROFESSIONAL BACKGROUND\n` +
      `----------------------\n` +
      `Current Position: ${formData.currentPosition || "Not provided"}\n` +
      `Company: ${formData.company || "Not provided"}\n` +
      `Professional Experience:\n${formData.experience || "Not provided"}\n\n` +
      `Education Background:\n${formData.education || "Not provided"}\n\n` +
      `Key Skills:\n${formData.skills || "Not provided"}\n\n` +
      `Languages: ${formData.languages || "Not provided"}\n\n` +
      
      `MOTIVATION & COMMITMENT\n` +
      `-----------------------\n` +
      `Why Join the 300 Mighty Men:\n${formData.whyJoin || "Not provided"}\n\n` +
      `Unique Contribution:\n${formData.contribution || "Not provided"}\n\n` +
      `Leadership Experience:\n${formData.leadershipExperience || "Not provided"}\n\n` +
      `References:\n${formData.references || "Not provided"}\n\n` +
      
      `CV ATTACHMENT\n` +
      `-------------\n` +
      `CV File: ${fileName || "No CV uploaded"}\n\n` +
      
      `===========================================\n` +
      `Application submitted via Worldwide Corporation website\n` +
      `Date: ${new Date().toLocaleString()}`
    );

    // Open default email client with pre-filled fields
    window.location.href = `mailto:John.Asfar@WorldwideCorporation.com?subject=${subject}&body=${body}`;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    
    try {
      // Send email
      sendEmail();
      
      // Mark as submitted
      setSubmitted(true);
      setSuccess(true);
      setLoading(false);
      
      console.log("Application submitted:", formData);
      if (cvFile) {
        console.log("CV File:", cvFile.name);
      }
    } catch (err) {
      setError("Failed to submit application. Please try again.");
      setLoading(false);
    }
  };

  const isStepValid = () => {
    switch (activeStep) {
      case 0:
        return formData.fullName && formData.email && formData.phone && formData.location;
      case 1:
        return formData.experience && formData.skills;
      case 2:
        return formData.whyJoin && formData.contribution;
      case 3:
        return formData.termsAccepted;
      default:
        return false;
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <Typography
                sx={{
                  color: "rgba(255,215,0,0.6)",
                  fontSize: "0.7rem",
                  letterSpacing: "3px",
                  textTransform: "uppercase",
                  mb: 1,
                }}
              >
                Personal Details
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <GlassTextField
                fullWidth
                required
                label="Full Name"
                name="fullName"
                size="small"
                value={formData.fullName}
                onChange={handleChange}
                placeholder="Enter your full name"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <GlassTextField
                fullWidth
                required
                label="Email Address"
                name="email"
                size="small"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@example.com"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <GlassTextField
                fullWidth
                required
                label="Phone Number"
                name="phone"
                size="small"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+27 12 345 6789"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <GlassTextField
                fullWidth
                label="Date of Birth"
                name="dateOfBirth"
                size="small"
                type="date"
                value={formData.dateOfBirth}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <GlassTextField
                fullWidth
                label="Age"
                name="age"
                size="small"
                type="number"
                value={formData.age}
                onChange={handleChange}
                placeholder="25"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <GlassTextField
                fullWidth
                label="Nationality"
                name="nationality"
                size="small"
                value={formData.nationality}
                onChange={handleChange}
                placeholder="South African"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <GlassTextField
                fullWidth
                required
                label="Location"
                name="location"
                size="small"
                value={formData.location}
                onChange={handleChange}
                placeholder="City, Country"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <GlassTextField
                fullWidth
                select
                label="Gender"
                name="gender"
                size="small"
                value={formData.gender}
                onChange={handleChange}
              >
                <MenuItem value="male">Male</MenuItem>
                <MenuItem value="female">Female</MenuItem>
                <MenuItem value="other">Other</MenuItem>
                <MenuItem value="prefer-not">Prefer not to say</MenuItem>
              </GlassTextField>
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <Typography
                sx={{
                  color: "rgba(255,215,0,0.6)",
                  fontSize: "0.7rem",
                  letterSpacing: "3px",
                  textTransform: "uppercase",
                  mb: 1,
                }}
              >
                Professional Background
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <GlassTextField
                fullWidth
                label="Current Position"
                name="currentPosition"
                size="small"
                value={formData.currentPosition}
                onChange={handleChange}
                placeholder="Senior Manager"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <GlassTextField
                fullWidth
                label="Company/Organization"
                name="company"
                size="small"
                value={formData.company}
                onChange={handleChange}
                placeholder="ABC Corporation"
              />
            </Grid>
            <Grid item xs={12}>
              <GlassTextField
                fullWidth
                required
                label="Professional Experience"
                name="experience"
                size="small"
                multiline
                rows={2}
                value={formData.experience}
                onChange={handleChange}
                placeholder="Describe your professional experience including key achievements..."
              />
            </Grid>
            <Grid item xs={12}>
              <GlassTextField
                fullWidth
                label="Education Background"
                name="education"
                size="small"
                multiline
                rows={2}
                value={formData.education}
                onChange={handleChange}
                placeholder="List your educational qualifications and certifications..."
              />
            </Grid>
            <Grid item xs={12}>
              <GlassTextField
                fullWidth
                required
                label="Key Skills & Competencies"
                name="skills"
                size="small"
                multiline
                rows={2}
                value={formData.skills}
                onChange={handleChange}
                placeholder="List your key skills, technical abilities, and areas of expertise..."
              />
            </Grid>
            <Grid item xs={12}>
              <GlassTextField
                fullWidth
                label="Languages Spoken"
                name="languages"
                size="small"
                value={formData.languages}
                onChange={handleChange}
                placeholder="English, Afrikaans, Zulu, etc."
              />
            </Grid>
            {/* CV Upload Field */}
            <Grid item xs={12}>
              <Box
                sx={{
                  border: "2px dashed rgba(255,215,0,0.2)",
                  borderRadius: 2,
                  p: 3,
                  textAlign: "center",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    borderColor: "rgba(255,215,0,0.4)",
                    background: "rgba(255,215,0,0.03)",
                  },
                }}
              >
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  id="cv-upload"
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
                <label htmlFor="cv-upload">
                  <Button
                    component="span"
                    variant="outlined"
                    startIcon={<CloudUploadIcon />}
                    sx={{
                      color: "#ffd700",
                      borderColor: "rgba(255,215,0,0.2)",
                      "&:hover": {
                        borderColor: "#ffd700",
                        background: "rgba(255,215,0,0.05)",
                      },
                    }}
                  >
                    Upload CV / Resume
                  </Button>
                </label>
                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.3)",
                    fontSize: "0.6rem",
                    mt: 1,
                  }}
                >
                  Accepted formats: PDF, DOC, DOCX (Max 5MB)
                </Typography>
                {fileName && (
                  <Box
                    sx={{
                      mt: 1.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      p: 1,
                      background: "rgba(255,215,0,0.05)",
                      borderRadius: 1,
                      border: "1px solid rgba(255,215,0,0.1)",
                    }}
                  >
                    <AttachFileIcon sx={{ color: "#ffd700", fontSize: "1rem" }} />
                    <Typography
                      sx={{
                        color: "#ffd700",
                        fontSize: "0.75rem",
                        fontWeight: "500",
                      }}
                    >
                      {fileName}
                    </Typography>
                    <Button
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCvFile(null);
                        setFileName("");
                        document.getElementById("cv-upload").value = "";
                      }}
                      sx={{
                        color: "rgba(255,255,255,0.3)",
                        fontSize: "0.5rem",
                        minWidth: "auto",
                        p: 0.5,
                        "&:hover": {
                          color: "#ff6b6b",
                        },
                      }}
                    >
                      ✕
                    </Button>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <Typography
                sx={{
                  color: "rgba(255,215,0,0.6)",
                  fontSize: "0.7rem",
                  letterSpacing: "3px",
                  textTransform: "uppercase",
                  mb: 1,
                }}
              >
                Motivation & Commitment
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <GlassTextField
                fullWidth
                required
                label="Why do you want to join the 300 Mighty Men?"
                name="whyJoin"
                size="small"
                multiline
                rows={3}
                value={formData.whyJoin}
                onChange={handleChange}
                placeholder="Share your motivation, vision, and what drives you to be part of this elite force..."
              />
            </Grid>
            <Grid item xs={12}>
              <GlassTextField
                fullWidth
                required
                label="What unique contribution can you bring?"
                name="contribution"
                size="small"
                multiline
                rows={3}
                value={formData.contribution}
                onChange={handleChange}
                placeholder="Describe the unique skills, perspective, or value you would bring..."
              />
            </Grid>
            <Grid item xs={12}>
              <GlassTextField
                fullWidth
                label="Leadership Experience"
                name="leadershipExperience"
                size="small"
                multiline
                rows={3}
                value={formData.leadershipExperience}
                onChange={handleChange}
                placeholder="Describe your leadership experience and examples..."
              />
            </Grid>
            <Grid item xs={12}>
              <GlassTextField
                fullWidth
                label="References (Optional)"
                name="references"
                size="small"
                multiline
                rows={2}
                value={formData.references}
                onChange={handleChange}
                placeholder="Provide professional references (name, title, contact)..."
              />
            </Grid>
          </Grid>
        );

      case 3:
        return (
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <Typography
                sx={{
                  color: "rgba(255,215,0,0.6)",
                  fontSize: "0.7rem",
                  letterSpacing: "3px",
                  textTransform: "uppercase",
                  mb: 1,
                }}
              >
                Review & Confirmation
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  background: "rgba(255,255,255,0.02)",
                  borderRadius: 2,
                  border: "1px solid rgba(255,255,255,0.04)",
                  maxHeight: "300px",
                  overflow: "auto",
                }}
              >
                <Grid container spacing={1.5}>
                  {Object.entries(formData)
                    .filter(([key, value]) => value && key !== "termsAccepted")
                    .map(([key, value]) => (
                      <Grid item xs={12} sm={6} key={key}>
                        <Typography
                          sx={{
                            color: "rgba(255,255,255,0.3)",
                            fontSize: "0.5rem",
                            textTransform: "uppercase",
                            letterSpacing: "1px",
                          }}
                        >
                          {key.replace(/([A-Z])/g, " $1").trim()}
                        </Typography>
                        <Typography
                          sx={{
                            color: "#ffffff",
                            fontSize: "0.8rem",
                            fontWeight: "500",
                            mb: 0.5,
                          }}
                        >
                          {typeof value === "string" && value.length > 50
                            ? `${value.substring(0, 50)}...`
                            : value}
                        </Typography>
                      </Grid>
                    ))}
                  <Grid item xs={12} sm={6}>
                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.3)",
                        fontSize: "0.5rem",
                        textTransform: "uppercase",
                        letterSpacing: "1px",
                      }}
                    >
                      CV Upload
                    </Typography>
                    <Typography
                      sx={{
                        color: fileName ? "#ffd700" : "rgba(255,255,255,0.3)",
                        fontSize: "0.8rem",
                        fontWeight: "500",
                        mb: 0.5,
                      }}
                    >
                      {fileName || "No CV uploaded"}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.termsAccepted}
                      onChange={handleChange}
                      name="termsAccepted"
                      sx={{
                        color: "rgba(255,255,255,0.3)",
                        "&.Mui-checked": {
                          color: "#ffd700",
                        },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: "0.8rem" }}>
                      I confirm all information is accurate and agree to the program terms.
                    </Typography>
                  }
                />
              </FormGroup>
            </Grid>
            {error && (
              <Grid item xs={12}>
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                  {error}
                </Alert>
              </Grid>
            )}
          </Grid>
        );

      default:
        return null;
    }
  };

  if (submitted && success) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          overflow: "hidden",
          background: "#0a0e1a",
          "&::before": {
            content: '""',
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url(/mightymenofdavid.png)`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            opacity: 0.55,
            zIndex: 0,
          },
          "&::after": {
            content: '""',
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "linear-gradient(160deg, rgba(10, 14, 26, 0.85) 0%, rgba(20, 25, 50, 0.8) 50%, rgba(36, 51, 138, 0.7) 100%)",
            zIndex: 0,
          },
        }}
      >
        <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
          <Zoom in timeout={500}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 4, md: 6 },
                borderRadius: 4,
                background: "rgba(255,255,255,0.04)",
                backdropFilter: "blur(30px)",
                border: "1px solid rgba(255,255,255,0.06)",
                textAlign: "center",
                boxShadow: "0 30px 80px rgba(0,0,0,0.5)",
              }}
            >
              <Box sx={{ fontSize: "4rem", mb: 3, animation: `${float} 3s ease-in-out infinite` }}>
                ⚔️
              </Box>
              <Typography sx={{ fontSize: "4rem", display: "block", mb: 2 }}>✅</Typography>
              <Typography
                variant="h4"
                sx={{
                  fontFamily: "'Georgia', serif",
                  fontWeight: "bold",
                  color: "#ffd700",
                  mb: 2,
                }}
              >
                Application Submitted!
              </Typography>
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.5)",
                  fontSize: "1rem",
                  maxWidth: "80%",
                  mx: "auto",
                  lineHeight: 2,
                }}
              >
                Thank you for applying to join the 300 Mighty Men. 
                Your application has been sent to our recruitment team.
              </Typography>
              {fileName && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    background: "rgba(255,215,0,0.05)",
                    borderRadius: 2,
                    border: "1px solid rgba(255,215,0,0.1)",
                  }}
                >
                  <Typography sx={{ color: "rgba(255,255,255,0.3)", fontSize: "0.7rem" }}>
                    CV Attached: <span style={{ color: "#ffd700" }}>{fileName}</span>
                  </Typography>
                </Box>
              )}
              <Box
                sx={{
                  mt: 4,
                  p: 3,
                  background: "rgba(255,215,0,0.03)",
                  borderRadius: 2,
                  border: "1px solid rgba(255,215,0,0.06)",
                }}
              >
                <Typography sx={{ color: "rgba(255,255,255,0.3)", fontSize: "0.7rem", letterSpacing: "2px" }}>
                  A copy of your application has been sent to your email
                </Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.2)", fontSize: "0.6rem", mt: 1 }}>
                  Our team will contact you within 48 hours
                </Typography>
              </Box>
              <Box sx={{ mt: 4, display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate("/login")}
                  sx={{
                    color: "#ffffff",
                    borderColor: "rgba(255,255,255,0.1)",
                    borderRadius: 3,
                    px: 4,
                    py: 1.5,
                    "&:hover": {
                      borderColor: "#ffd700",
                      background: "rgba(255,215,0,0.05)",
                    },
                  }}
                >
                  Back to Login
                </Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    setSubmitted(false);
                    setSuccess(false);
                    setActiveStep(0);
                    setCvFile(null);
                    setFileName("");
                    setFormData({
                      fullName: "",
                      email: "",
                      phone: "",
                      age: "",
                      dateOfBirth: "",
                      nationality: "",
                      location: "",
                      gender: "",
                      currentPosition: "",
                      company: "",
                      experience: "",
                      education: "",
                      skills: "",
                      languages: "",
                      whyJoin: "",
                      contribution: "",
                      leadershipExperience: "",
                      termsAccepted: false,
                      references: "",
                    });
                  }}
                  sx={{
                    background: "linear-gradient(135deg, #ffd700, #f5a623)",
                    color: "#0a0e1a",
                    borderRadius: 3,
                    px: 4,
                    py: 1.5,
                    fontWeight: "bold",
                    "&:hover": {
                      background: "linear-gradient(135deg, #ffed4a, #ffd700)",
                      boxShadow: "0 8px 35px rgba(255,215,0,0.2)",
                    },
                  }}
                >
                  Submit Another
                </Button>
              </Box>
            </Paper>
          </Zoom>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        py: 4,
        background: "#0a0e1a",
      }}
    >
      {/* Top Navigation - Clean with only Back button and Chip */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          py: 1.5,
          px: { xs: 2, md: 4 },
          background: "rgba(10, 14, 26, 0.9)",
          backdropFilter: "blur(20px)",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          boxShadow: "0 4px 30px rgba(0,0,0,0.3)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Button
          onClick={() => navigate("/login")}
          startIcon={<span>←</span>}
          sx={{
            color: "rgba(255,255,255,0.4)",
            fontSize: "0.7rem",
            letterSpacing: "1px",
            textTransform: "none",
            "&:hover": {
              color: "#ffffff",
              background: "rgba(255,255,255,0.05)",
            },
          }}
        >
          Back to Login
        </Button>
        <Chip
          icon={<span style={{ fontSize: "0.8rem" }}>⚔️</span>}
          label="300 Mighty Men"
          sx={{
            background: "rgba(255,215,0,0.08)",
            color: "#ffd700",
            border: "1px solid rgba(255,215,0,0.1)",
            fontSize: "0.5rem",
            letterSpacing: "2px",
            fontWeight: "600",
            "& .MuiChip-icon": {
              color: "#ffd700",
            },
          }}
        />
      </Box>

      {/* Main Content - Two Column Layout */}
      <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1, mt: 10 }}>
        <Grid container spacing={0} sx={{ minHeight: "80vh" }}>
          {/* Left Side - Image Panel with Logo on Top */}
          <Grid item xs={12} lg={5}>
            <LeftPanel>
              {/* Logo on top-left of image - Larger */}
              <LogoContainer>
                <Box sx={{ mb: 0.5 }}>
                  <Logo size="large" variant="icon" />
                </Box>
                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.15)",
                    fontSize: "0.35rem",
                    letterSpacing: "6px",
                    textTransform: "uppercase",
                    fontWeight: "500",
                  }}
                >
                  Since 1995
                </Typography>
              </LogoContainer>
            </LeftPanel>
          </Grid>

          {/* Right Side - Form Panel */}
          <Grid item xs={12} lg={7}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 3, sm: 4, md: 5 },
                borderRadius: { xs: 0, lg: "0 4px 4px 0" },
                background: "rgba(255,255,255,0.04)",
                backdropFilter: "blur(30px)",
                border: "1px solid rgba(255,255,255,0.06)",
                borderLeft: { xs: "none", lg: "1px solid rgba(255,255,255,0.06)" },
                boxShadow: "0 30px 80px rgba(0,0,0,0.5)",
                height: "100%",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "4px",
                  background: "linear-gradient(90deg, #ffd700, #ffed4a, #ffd700, #ffed4a)",
                  backgroundSize: "200% auto",
                  animation: `${pulseGlow} 3s ease-in-out infinite`,
                },
              }}
            >
              <Box>
                {/* Header - "Join the 300" */}
                <Box sx={{ textAlign: "center", mb: 3 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontFamily: "'Georgia', serif",
                      fontWeight: "bold",
                      color: "#ffffff",
                      mb: 0.5,
                    }}
                  >
                    Join the 300
                  </Typography>
                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.25)",
                      fontSize: "0.6rem",
                      letterSpacing: "4px",
                      textTransform: "uppercase",
                    }}
                  >
                    Complete the application form below
                  </Typography>
                  <Typography
                    sx={{
                      color: "rgba(255,215,0,0.3)",
                      fontSize: "0.5rem",
                      letterSpacing: "1px",
                      mt: 1,
                    }}
                  >
                    Applications are sent to: John.Asfar@WorldwideCorporation.com
                  </Typography>
                </Box>

                <Divider sx={{ borderColor: "rgba(255,255,255,0.05)", mb: 3 }} />

                {/* Stepper */}
                <Stepper
                  activeStep={activeStep}
                  orientation="vertical"
                  sx={{
                    "& .MuiStepLabel-label": {
                      color: "rgba(255,255,255,0.3)",
                      fontWeight: "500",
                      fontSize: "0.85rem",
                      "&.Mui-active": {
                        color: "#ffd700",
                      },
                      "&.Mui-completed": {
                        color: "#ffd700",
                      },
                    },
                    "& .MuiStepIcon-root": {
                      color: "rgba(255,255,255,0.1)",
                      "&.Mui-active": {
                        color: "#ffd700",
                      },
                      "&.Mui-completed": {
                        color: "#ffd700",
                      },
                    },
                    "& .MuiStepContent-root": {
                      borderColor: "rgba(255,255,255,0.05)",
                    },
                  }}
                >
                  {steps.map((step, index) => (
                    <Step key={step.label}>
                      <StepLabel>
                        <Typography sx={{ color: "#ffffff", fontSize: "0.9rem" }}>
                          {step.label}
                        </Typography>
                      </StepLabel>
                      <StepContent>
                        {renderStepContent(index)}
                        <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
                          <Button
                            disabled={index === 0}
                            onClick={handleBack}
                            sx={{
                              color: "rgba(255,255,255,0.4)",
                              fontSize: "0.75rem",
                              "&:hover": {
                                color: "#ffffff",
                              },
                            }}
                          >
                            Back
                          </Button>
                          <Button
                            variant="contained"
                            onClick={index === steps.length - 1 ? handleSubmit : handleNext}
                            disabled={!isStepValid() || loading}
                            sx={{
                              ml: "auto",
                              background: "linear-gradient(135deg, #ffd700, #f5a623)",
                              color: "#0a0e1a",
                              px: 4,
                              py: 1.2,
                              borderRadius: 2,
                              fontWeight: "bold",
                              fontSize: "0.75rem",
                              letterSpacing: "1px",
                              "&:hover": {
                                background: "linear-gradient(135deg, #ffed4a, #ffd700)",
                                boxShadow: "0 8px 35px rgba(255,215,0,0.2)",
                              },
                              "&:disabled": {
                                background: "rgba(255,255,255,0.05)",
                                color: "rgba(255,255,255,0.2)",
                              },
                            }}
                          >
                            {loading
                              ? "SUBMITTING..."
                              : index === steps.length - 1
                              ? "Submit Application"
                              : "Continue →"}
                          </Button>
                        </Box>
                      </StepContent>
                    </Step>
                  ))}
                </Stepper>

                {/* Progress Indicator */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 1,
                    mt: 2,
                  }}
                >
                  {steps.map((_, index) => (
                    <Box
                      key={index}
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background:
                          index === activeStep
                            ? "#ffd700"
                            : index < activeStep
                            ? "rgba(255,215,0,0.3)"
                            : "rgba(255,255,255,0.05)",
                        transition: "all 0.3s ease",
                        boxShadow:
                          index === activeStep
                            ? "0 0 20px rgba(255,215,0,0.2)"
                            : "none",
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Footer */}
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          py: 1.2,
          px: { xs: 2, md: 4 },
          background: "rgba(10, 14, 26, 0.8)",
          backdropFilter: "blur(20px)",
          borderTop: "1px solid rgba(255,255,255,0.02)",
          textAlign: "center",
        }}
      >
        <Typography
          sx={{
            color: "rgba(255,255,255,0.03)",
            fontSize: "0.35rem",
            letterSpacing: "6px",
            textTransform: "uppercase",
          }}
        >
          Worldwide Corporation • Since 1995 • The 300 Mighty Men
        </Typography>
      </Box>
    </Box>
  );
};

export default Application;